import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';
import { InferType } from 'yup';

import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import { TaskPriority } from '@graphql/generated';
import { useHover } from '@hooks/useHover';
import { editTaskPrioritySchema } from '@schemas/editTaskPrioritySchema';
import theme from '@themes/theme';

export type FormValues = InferType<typeof editTaskPrioritySchema>;

// Dropdown row (TA-9005): an unselected priority option tints to grey02 on
// hover (the modal surface is grey01, so grey01 would be invisible); the
// selected one keeps its plain background. Extracted so the hover hook is
// not called inside the `.map()` callback (Rules of Hooks).
const PriorityOptionRow: React.FC<{
  isSelected: boolean;
  isLast: boolean;
  onPress: () => void;
  accessibilityLabel: string;
  children: React.ReactNode;
}> = ({ isSelected, isLast, onPress, accessibilityLabel, children }) => {
  const { isHovered, hoverProps } = useHover({ disabled: isSelected });
  return (
    <TouchableOpacity
      {...hoverProps}
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}>
      <Box
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        padding='xs'
        borderRadius='xs'
        backgroundColor={isHovered ? 'grey02' : undefined}
        marginBottom={!isLast ? 's' : undefined}>
        {children}
      </Box>
    </TouchableOpacity>
  );
};

interface TaskDetailEditPriorityProps {
  loading: boolean;
  isEditTaskPriority: boolean;
  onClose: () => void;
}

export const TaskDetailEditPriority: React.FC<TaskDetailEditPriorityProps> = ({
  loading,
  isEditTaskPriority,
  onClose,
}) => {
  const { t } = useTranslation();
  const { values, submitForm, setFieldValue } = useFormikContext<FormValues>();
  const { priority } = values;

  const priorities: { title: string; value: TaskPriority }[] = [
    { title: t('models:tasks.create.priority.low'), value: TaskPriority.Low },
    {
      title: t('models:tasks.create.priority.medium'),
      value: TaskPriority.Medium,
    },
    { title: t('models:tasks.create.priority.high'), value: TaskPriority.High },
  ];

  const [selectedPriority, setSelectedPriority] = useState(priority);

  const getColor = (value: string) => {
    let textColor;
    switch (value) {
      case TaskPriority.High:
        textColor = theme.colors.alertRed;
        break;

      case TaskPriority.Medium:
        textColor = theme.colors.yellowVivid;
        break;

      default:
        textColor = theme.colors.textSecondary;
    }
    return textColor;
  };

  return (
    <WebModal
      accessibilityLabel={t('models:tasks.create.priority.title')}
      visible={isEditTaskPriority}
      width={200}
      height={200}
      title={t('models:tasks.create.priority.title')}
      titleVariant='labelLarge'
      titleColor='textPrimary'
      marginTop='m'
      marginHorizontal='l'
      onClose={onClose}>
      <Box marginTop='m' marginHorizontal='l'>
        {priorities.map((i, index) => {
          const { title, value } = i;
          return (
            <PriorityOptionRow
              key={title}
              isSelected={value === selectedPriority}
              isLast={index === priorities.length - 1}
              accessibilityLabel={title}
              onPress={() => {
                setSelectedPriority(value);
                setFieldValue('priority', value);
                submitForm();
              }}>
              <Box flex={1}>
                <Text
                  variant='body'
                  style={{
                    textTransform: 'capitalize',
                    color: getColor(value),
                  }}>
                  {title}
                </Text>
              </Box>
              <Box mr='m'>
                {value === TaskPriority.High && (
                  <Icon name='StatusHigh' variant='l' color='textPrimary' />
                )}
                {value === TaskPriority.Medium && (
                  <Icon name='StatusMedium' variant='l' color='textPrimary' />
                )}
              </Box>
              <Icon
                variant='s'
                name='Check'
                color={
                  value == selectedPriority ? 'greenSecondary' : 'transparent'
                }
                accessibilityLabel={`SelectedRole-${i}`}
              />
            </PriorityOptionRow>
          );
        })}
        {loading && <ActivityIndicatorLoading />}
      </Box>
    </WebModal>
  );
};
